import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { performCmdbPostRequest } from '@atlassian/jira-servicedesk-cmdb-client/src/fetch.tsx';
import { getCmdbObjectQueryLanguageTotalCountUrl } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/object.tsx';
import type { WorkspaceId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type {
	ActionApi,
	AsyncSuccess,
	SearchStateValue,
	UncappedNumObjects,
} from '../../../../common/types/index.tsx';
import type { SearchObjectTotalCountResponse } from '../../../../common/types/responses.tsx';
import {
	checkIsSupportedSearchScope,
	generateQlQueryForSearchScope,
} from '../../../../common/utils/index.tsx';

export const fetchCmdbObjectQueryLanguageTotalCount = async (
	workspaceId: WorkspaceId,
	qlQuery: string,
) =>
	performCmdbPostRequest<SearchObjectTotalCountResponse>(
		getCmdbObjectQueryLanguageTotalCountUrl(workspaceId),
		{ body: JSON.stringify({ qlQuery }) },
	);

const updateUncappedNumObjectsState = (
	lastSearch: AsyncSuccess<SearchStateValue>,
	uncappedNumObjects: UncappedNumObjects,
) => ({
	lastSearch: {
		...lastSearch,
		value: {
			...lastSearch.value,
			result: {
				...lastSearch.value.result,
				uncappedNumObjects,
			},
		},
	},
});

export const fetchUncappedCount =
	(): ActionApi =>
	async ({ getState, setState }, { workspaceId, createAnalyticsEvent }) => {
		const { lastSearch, currentSearchScope } = getState();
		if (lastSearch.type !== 'success' || !currentSearchScope) {
			return;
		}
		setState(
			updateUncappedNumObjectsState(lastSearch, {
				totalCount: undefined,
				loading: true,
				error: undefined,
			}),
		);
		try {
			const lastQlQueryString = lastSearch.value.result.qlQuery;
			const { objectTypeId, objectSchemaId, isObjectTypeInherited } =
				checkIsSupportedSearchScope(currentSearchScope);
			const qlQuery = generateQlQueryForSearchScope(lastQlQueryString, {
				objectTypeId,
				objectSchemaId,
				isObjectTypeInherited,
			});
			const response = await fetchCmdbObjectQueryLanguageTotalCount(workspaceId, qlQuery);
			fireTrackAnalytics(createAnalyticsEvent({}), 'fetchUncappedCount succeeded');
			setState(
				updateUncappedNumObjectsState(lastSearch, {
					totalCount: response.totalCount,
					loading: false,
					error: undefined,
				}),
			);
		} catch (err: unknown) {
			const typesafeError = err instanceof Error ? err : new Error('Unknown error');
			setState(
				updateUncappedNumObjectsState(lastSearch, {
					totalCount: undefined,
					loading: false,
					error: typesafeError,
				}),
			);
			fireCmdbErrorAnalytics({
				meta: {
					id: 'fetchUncappedCount',
					packageName: 'jiraServicedeskInsightObjectSearchStore',
					teamName: 'aurum',
				},
				error: typesafeError,
			});
		}
	};
